// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Draws a display list through a camera.
 *
 * Everything is drawn in board millimetres with the canvas transform doing the
 * scaling, so a 0.25 mm track is asked for as 0.25 at every zoom. Because we own
 * both the renderer and the overlay, the diff annotations land exactly on the
 * geometry — the alignment problem §6.7 anticipated with a third-party renderer
 * does not exist here.
 */

import { intersects, type BBox } from '../model/bbox.ts';
import type { Camera, Viewport } from './camera.ts';
import type { DisplayList, ShapeItem, TextItem } from './displaylist.ts';
import { layerStyle, UNSUPPORTED_LAYER } from './palette.ts';

export interface RenderOptions {
  /** Only these layers are drawn. */
  visibleLayers: ReadonlySet<string>;
  /** Multiplies every colour's alpha — the onion-skin control. */
  opacity: number;
  /**
   * When set, objects **not** in this set are drawn at `dimmedOpacity`. Used to
   * push unchanged copper into the background so the changes stand out.
   */
  emphasise?: ReadonlySet<string>;
  dimmedOpacity: number;
  /** Draw text labels. Off at low zoom, where they are unreadable anyway. */
  drawText: boolean;
  /** Uniform colour override, used to tint a whole board in onion-skin mode. */
  tint?: string;
}

export const DEFAULT_RENDER_OPTIONS: RenderOptions = {
  visibleLayers: new Set(),
  opacity: 1,
  dimmedOpacity: 0.18,
  drawText: true,
};

/** Below this many screen pixels an item is too small to be worth drawing. */
const MIN_FEATURE_PX = 0.35;
/** Text smaller than this on screen is illegible; skip it rather than smear it. */
const MIN_TEXT_PX = 5;

export interface RenderStats {
  shapesDrawn: number;
  shapesCulled: number;
  textsDrawn: number;
  ms: number;
}

export class BoardRenderer {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly dpr: number;

  constructor(ctx: CanvasRenderingContext2D, dpr = 1) {
    this.ctx = ctx;
    this.dpr = dpr;
  }

  /**
   * Draw `list` through `camera`. The caller is responsible for clearing and for
   * calling this once per board when compositing an onion-skin.
   */
  draw(list: DisplayList, camera: Camera, view: Viewport, options: RenderOptions): RenderStats {
    const started = performance.now();
    const ctx = this.ctx;
    const visible = camera.visibleBox(view);
    const minSize = camera.pixelSize(MIN_FEATURE_PX);
    const hairline = camera.pixelSize(1);

    let shapesDrawn = 0;
    let shapesCulled = 0;
    let textsDrawn = 0;

    ctx.save();
    camera.applyTo(ctx, this.dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    let currentLayer = '';
    let currentColor = '';

    for (const item of list.shapes) {
      if (!options.visibleLayers.has(item.layer)) continue;
      if (!intersects(item.bbox, visible)) {
        shapesCulled++;
        continue;
      }
      if (!isWorthDrawing(item.bbox, minSize)) {
        shapesCulled++;
        continue;
      }

      if (item.layer !== currentLayer || item.color !== undefined) {
        currentLayer = item.layer;
        currentColor = options.tint ?? item.color ?? layerStyle(item.layer).color;
      }

      const dimmed = options.emphasise !== undefined && !options.emphasise.has(item.objectId);
      // Holes are punched, not painted: they keep their own colour so a via
      // still reads as a via when the rest of the board is tinted.
      const color = item.layer === '~holes' ? layerStyle(item.layer).color : currentColor;
      ctx.globalAlpha = options.opacity * (dimmed ? options.dimmedOpacity : 1);

      if (item.layer === UNSUPPORTED_LAYER) {
        this.drawUnsupported(item, hairline);
        shapesDrawn++;
        continue;
      }

      if (item.filled) {
        ctx.fillStyle = color;
        ctx.fill(item.path);
      } else {
        ctx.strokeStyle = color;
        ctx.lineWidth = item.width > 0 ? item.width : hairline;
        ctx.stroke(item.path);
      }
      shapesDrawn++;
    }

    if (options.drawText) {
      for (const item of list.texts) {
        if (!options.visibleLayers.has(item.layer)) continue;
        if (!intersects(item.bbox, visible)) continue;
        if (item.size * camera.scale < MIN_TEXT_PX) continue;
        const dimmed = options.emphasise !== undefined && !options.emphasise.has(item.objectId);
        ctx.globalAlpha = options.opacity * (dimmed ? options.dimmedOpacity : 1);
        ctx.fillStyle = options.tint ?? layerStyle(item.layer).color;
        this.drawText(item);
        textsDrawn++;
      }
    }

    ctx.restore();
    return { shapesDrawn, shapesCulled, textsDrawn, ms: performance.now() - started };
  }

  /**
   * Magenta hatched box for content the renderer cannot draw (§6.7). The user
   * must never read blank board as "nothing there".
   */
  private drawUnsupported(item: ShapeItem, hairline: number): void {
    const ctx = this.ctx;
    const b = item.bbox;
    ctx.strokeStyle = layerStyle(UNSUPPORTED_LAYER).color;
    ctx.lineWidth = hairline;
    ctx.stroke(item.path);

    ctx.save();
    ctx.beginPath();
    ctx.rect(b.minX, b.minY, b.maxX - b.minX, b.maxY - b.minY);
    ctx.clip();
    ctx.globalAlpha *= 0.5;
    ctx.beginPath();
    const h = b.maxY - b.minY;
    const step = Math.max((b.maxX - b.minX) / 8, hairline * 6);
    for (let x = b.minX - h; x < b.maxX; x += step) {
      ctx.moveTo(x, b.minY);
      ctx.lineTo(x + h, b.maxY);
    }
    ctx.stroke();
    ctx.restore();
  }

  private drawText(item: TextItem): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.translate(item.x, item.y);
    // KiCad's positive rotation is counter-clockwise on a Y-down board, which is
    // the negative direction for the canvas transform.
    if (item.angle !== 0) ctx.rotate((-item.angle * Math.PI) / 180);
    if (item.mirror) ctx.scale(-1, 1);

    ctx.font = `${item.size}px ui-monospace, monospace`;
    ctx.textAlign = item.hAlign;
    ctx.textBaseline = item.vAlign === 'middle' ? 'middle' : item.vAlign;

    const lineHeight = item.size * 1.2;
    const total = (item.lines.length - 1) * lineHeight;
    const first = item.vAlign === 'middle' ? -total / 2 : item.vAlign === 'bottom' ? -total : 0;
    for (let i = 0; i < item.lines.length; i++) {
      ctx.fillText(item.lines[i]!, 0, first + i * lineHeight);
    }
    ctx.restore();
  }
}

function isWorthDrawing(b: BBox, minSize: number): boolean {
  return b.maxX - b.minX >= minSize || b.maxY - b.minY >= minSize;
}

/** Layers a board actually draws on, in paint order, excluding empty ones. */
export function defaultVisibleLayers(list: DisplayList): Set<string> {
  return new Set(list.layers.filter((l) => layerStyle(l).visibleByDefault));
}
